import { GameScene } from '@Core/index';
import ReelSymbol from '@Core/Reels/ReelSymbol';
import { getSymbolRoles } from '@Core/Utils/SymbolUtils';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { ISpinOutcome } from '@GFTypes/GameStoreTypes';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { ExpandingSymbol } from '../ExpandingSymbol';
import { EExpandingSymbolAnimationModifiers, EExpandingSymbolAnimations } from '../ExpandingSymbolAnimations';
import { EXPANDING_SYMBOL_ROLES } from '../ExpandingSymbolFeature.constants';
import { IPositionOnReels, ISymbolInfo, SymbolExpansionStrategy } from '../ExpandingSymbolFeature.types';
import { Container, Point } from 'pixi.js';

export class VerticalExpansionStrategy implements SymbolExpansionStrategy {
    public symbolIds: number[] = [];

    constructor(
        private readonly scene: GameScene,
        private readonly topLayerContainer: Container,
        private readonly isVisibleAfterExpanding: boolean = true
    ) {}

    public getWinningExpandingSymbols(outcome: ISpinOutcome): ISymbolInfo[] {
        const winningExpandingSymbols: ISymbolInfo[] = [];
        const visibleSymbolsPerStep = outcome.steps.map((step) => step.finalSymbols.map((reel) => reel.slice(1, -1)));
        visibleSymbolsPerStep.at(0)?.forEach((reel, reelIndex) => {
            const visibleSymbolIndex = reel.findIndex((symbolId) => {
                const expandingSymbolId = this.symbolIds.find((id) => id === symbolId);
                return expandingSymbolId && this.isExpandingSymbolWinning(expandingSymbolId, reelIndex, visibleSymbolsPerStep.at(-1) ?? []);
            });
            if (visibleSymbolIndex === -1) return;
            winningExpandingSymbols.push({
                positionOnReel: { reelIndex, visibleSymbolIndex },
                symbolId: reel[visibleSymbolIndex]
            });
        });
        return winningExpandingSymbols;
    }

    public getInitialExpandingSymbols(loopWinAnimation = false, showOnlyHighestMultiplier = false): ExpandingSymbol[] {
        const expandingSymbols: ExpandingSymbol[] = [];
        const numberOfReels = this.scene.game.gameStore.get(EGameStoreKeys.NumberOfReels);
        const numberOfVisibleSymbolsPerReel = this.scene.game.gameStore.get(EGameStoreKeys.NumberOfSymbolsPerReel);

        for (let reelIndex = 0; reelIndex < numberOfReels; reelIndex++) {
            const coveredPositions = Array.from({ length: numberOfVisibleSymbolsPerReel }, (_, visibleSymbolIndex) => ({
                reelIndex,
                visibleSymbolIndex
            }));
            const symbol = new ExpandingSymbol(
                this.symbolIds[0],
                coveredPositions,
                coveredPositions,
                showOnlyHighestMultiplier,
                this.scene,
                this.topLayerContainer,
                this,
                this.isVisibleAfterExpanding,
                this.isVisibleAfterExpanding
            );
            if (loopWinAnimation) symbol.assignEventSubscriptions({ event: EGameStates.WIN_STATE, fn: symbol.startWinAnimation.bind(symbol, true) });
            else {
                symbol.assignEventSubscriptions(
                    { event: EGameEvents.CYCLE_WINLINE, fn: symbol.startWinAnimationIfWinning, context: symbol },
                    { event: EGameEvents.CYCLE_WAYS, fn: symbol.startWinAnimationIfWinning, context: symbol }
                );
            }
            if (!this.isVisibleAfterExpanding)
                symbol.assignEventSubscriptions({ event: EGameStates.POST_EXPANDING_SYMBOL_STATE, fn: symbol.hide, context: symbol });

            expandingSymbols.push(symbol);
        }
        return expandingSymbols;
    }

    public getExpandingAnimationName(expandingSymbol: ExpandingSymbol): string {
        const expandingAnimation = this.getAnimationVariant(EExpandingSymbolAnimations.SYMBOL_EXPANDING, expandingSymbol);
        return expandingAnimation;
    }

    public getSpinePosition({ coveredPositions }: ExpandingSymbol, visibleSymbols: ReelSymbol[][]): Point {
        return this.getLocalMiddlePoint(coveredPositions, visibleSymbols);
    }

    private isExpandingSymbolWinning(expandingSymbolId: number, reelIndex: number, visibleSymbolsLastStep: number[][]): boolean {
        const symbolHasCurrentlyExpandingSymbolRole = getSymbolRoles(this.scene.game.gameStore, expandingSymbolId).some((role) =>
            EXPANDING_SYMBOL_ROLES.includes(role)
        );
        return (symbolHasCurrentlyExpandingSymbolRole && visibleSymbolsLastStep.at(reelIndex)?.every((symbolId) => symbolId === expandingSymbolId)) ?? false;
    }

    private getLocalMiddlePoint(coveredSymbols: IPositionOnReels[], visibleSymbols: ReelSymbol[][]): Point {
        let sumX = 0;
        let sumY = 0;
        let count = 0;

        for (const pos of coveredSymbols) {
            const symbol = visibleSymbols[pos.reelIndex][pos.visibleSymbolIndex];
            if (!symbol) continue;

            sumX += symbol.x;
            sumY += symbol.y;
            count++;
        }

        if (count === 0) {
            return new Point(0, 0);
        }

        return new Point(sumX / count, sumY / count);
    }

    public getAnimationVariant(animation: EExpandingSymbolAnimations, expandingSymbol: ExpandingSymbol): string {
        const multiplier = expandingSymbol.winningExpandingSymbolIndex + 1;
        const shouldShowMultiplier = multiplier > 1 && (!expandingSymbol.showOnlyHighestMultiplier || expandingSymbol.isLastWinningExpandingSymbol);
        const multiplierModifier = shouldShowMultiplier ? EExpandingSymbolAnimationModifiers.MULTIPLIER.replace('$', multiplier.toString()) : undefined;
        const reelIndex = [...new Set(expandingSymbol.originPositions.map((position) => position.reelIndex + 1))][0];
        const reelModifier = EExpandingSymbolAnimationModifiers.REEL.replace('$', reelIndex.toString());
        const rowIndex = expandingSymbol.visibleRowIndex + 1;
        const rowModifier = EExpandingSymbolAnimationModifiers.ROW.replace('$', rowIndex.toString());
        const modifiers = [multiplierModifier, reelModifier, rowModifier].filter(Boolean) as string[];
        const animationVariant = this.getAnimation(animation, expandingSymbol, modifiers);
        return animationVariant;
    }

    public getAnimation(base: EExpandingSymbolAnimations, expandingSymbol: ExpandingSymbol, modifiers: string[] = []): string {
        const modifiersSet = new Set(modifiers);
        const variants = expandingSymbol.animationVariants.get(base);
        if (!variants) return base;

        let bestVariant: string = '';
        let bestScore = 0;

        for (const [keySet, names] of variants) {
            const matchCount = [...keySet].filter((m) => modifiersSet.has(m)).length;
            const score = keySet.size > 0 ? matchCount / keySet.size : 0;
            if (score > bestScore || (score === 0 && bestScore === -1)) {
                bestScore = score;
                bestVariant = names[0];
            }
        }
        bestVariant ||= base;
        return bestVariant;
    }
}
